import { Text } from '@quark/typography'
import { Flex } from '@quark/layout'
import React from 'react'
import { ProductCardProps } from '@interfaces/home'
import { BrandVars } from '@quark/theme'
import { Tag } from '@quark/tag'
import StrapiMedia from '@components/StrapiMedia'
import { ArrowRight } from '@quark/icons'
import Image from 'next/image'
import { Button } from '@quark/button'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import styles from './Hero.module.scss'
import CardContent from './CardContent'

const HeroCard = ({ card, onClick }: { card: ProductCardProps; onClick?: Function }) => (
  <div
    className={`${styles.heroCard} ${card?.isFeatured ? styles.featured : ''}`}
    style={{
      ['--backgroundGradient' as any]: card?.isFeatured
        ? `linear-gradient(180deg, ${BrandVars.Surface['base/elevated']} 50%, ${BrandVars.Surface['accent/Violet/Violet-400']} 239.03%)`
        : BrandVars.Surface['base/elevated'],
      ['--cardHoverBorder' as any]: BrandVars.Surface['accent/Violet/Violet-600'],
      ['--iconBorder' as any]: BrandVars.Icon['base/primary'],
      ['--iconBg' as any]: BrandVars.Surface['base/overlay'],
      ['--hoverIconBg' as any]: BrandVars.Icon['base/primary'],
      boxShadow: BrandVars.Shadow['Neutral FX/Medium'],
    }}
    onClick={() =>
      onClick?.(card?.eventProperties, card?.mobileUrl ?? card?.url ?? '/', card?.url ?? '/', card?.urlInNewTab)
    }>
    <Flex
      size="fullSize"
      flexDirection="column"
      justifyContent="space-between"
      gap={{ sm: 'spacing-32', md: 'spacing-32' }}>
      <Flex flexDirection="column" size="fullSize" gap={{ lg: 'spacing-12', md: 'spacing-12', sm: 'spacing-8' }}>
        <CardContent
          desktopContent={card?.desktopContent}
          mobileContent={card?.mobileContent}
          isFeatured={card?.isFeatured}
        />
        <PropertyControlledComponent controllerProperty={!!card?.tag} fallback={null}>
          <Tag
            py="spacing-8"
            paddingLeft="spacing-8"
            paddingRight="spacing-12"
            background={`linear-gradient(90deg, ${BrandVars.Surface['accent/Violet/Violet-600']} 0%, ${BrandVars.Surface['accent/Pink/pink-100']} 98.77%);`}>
            <Flex gap="spacing-6" alignItems="center">
              <Image
                className={styles.tagIcon}
                src="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/hero_section_tick_f6f995abe0.svg"
                alt="Tag Icon"
                width="20"
                height="20"
              />
              <Text
                fontStyle={{ lg: 'um_text-sm/medium', md: 'um_text-sm/medium', sm: 'um_text-2xs/medium' }}
                color="base/primary">
                {card?.tag}
              </Text>
            </Flex>
          </Tag>
        </PropertyControlledComponent>
      </Flex>
      <ArrowRight />
    </Flex>
    <PropertyControlledComponent controllerProperty={!!card?.image} fallback={null}>
      <div className={styles.heroCardIcon}>
        <StrapiMedia
          mediaData={card?.image ?? undefined}
          objectFit="contain"
          size="fullWidth"
          alt={card?.name}
          imgProps={{
            alt: card?.image?.alternativeText ?? card?.name ?? '',
            loading: 'eager',
            fetchPriority: 'high',
          }}
          videoProps={{ autoPlay: true, muted: true }}
        />
      </div>
    </PropertyControlledComponent>
    <PropertyControlledComponent controllerProperty={card?.isFeatured} fallback={null}>
      <div className={styles.featuredHoverCardContainer}>
        <div className={styles.featuredHoverCard}>
          <Flex flexDirection="column" gap="spacing-20" size="fullWidth">
            <Text
              fontStyle={{ lg: 'um_text-2xl/bold', md: 'um_text-xl/bold', sm: 'um_text-base/bold' }}
              color="base/primary"
              whiteSpace="pre">
              {card?.featuredData?.heading}
            </Text>
            <Flex flexDirection="column" gap="spacing-12" size="fullWidth">
              {card?.featuredData?.features?.map(feature => (
                <Tag
                  width="full"
                  justifyContent="start"
                  key={feature}
                  py="spacing-8"
                  paddingLeft="spacing-8"
                  paddingRight="spacing-12"
                  background={`linear-gradient(90deg, ${BrandVars.Surface['accent/Violet/Violet-600']} 0%, ${BrandVars.Surface['accent/Pink/pink-100']} 98.77%);`}>
                  <Image
                    className={styles.tagIcon}
                    src="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/hero_section_tick_f6f995abe0.svg"
                    alt="Tag Icon"
                    width="20"
                    height="20"
                  />
                  <Text
                    textAlign="left"
                    fontStyle={{ lg: 'um_text-sm/medium', md: 'um_text-sm/medium', sm: 'um_text-2xs/medium' }}
                    color="base/primary">
                    {feature}
                  </Text>
                </Tag>
              ))}
            </Flex>
          </Flex>
        </div>
        <div className={styles.featuredHoverCardButton}>
          <Button type="solid" size="block">
            <Text fontStyle="um_text-base/medium" color="base/inverted primary">
              {card?.featuredData?.ctaText}
            </Text>
          </Button>
        </div>
      </div>
    </PropertyControlledComponent>
  </div>
)

export default HeroCard
